import { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, CircularProgress, Container, Typography } from '@mui/material';
import { db, storage } from '../firebase';
import './GalleryPage.css';

const photosCollection = collection(db, 'tripGalleryPhotos');

function GalleryPage() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const q = query(photosCollection, orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPhotos(next);
        setLoading(false);
      },
      () => {
        setLoading(false);
        setMessage('Could not load photos. Check Firestore rules and configuration.');
      }
    );

    return () => unsubscribe();
  }, []);

  const photoCountLabel = useMemo(
    () => `${photos.length} photo${photos.length === 1 ? '' : 's'}`,
    [photos.length]
  );

  const handleUpload = async (event) => {
    const fileList = event.target.files;
    if (!fileList || !fileList.length) return;

    setUploading(true);
    setMessage('');
    const files = Array.from(fileList).filter((file) => file.type.startsWith('image/'));

    try {
      for (const file of files) {
        const safeName = file.name.replace(/\s+/g, '-').toLowerCase();
        const storagePath = `trip-gallery/${Date.now()}-${safeName}`;
        const storageRef = ref(storage, storagePath);

        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);

        await addDoc(photosCollection, {
          name: file.name,
          url,
          storagePath,
          contentType: file.type,
          size: file.size,
          createdAt: serverTimestamp(),
        });
      }

      setMessage('Upload complete.');
    } catch (error) {
      setMessage('Upload failed. Check Firebase config, Storage rules, and Firestore rules.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDownload = async (photo) => {
    try {
      const response = await fetch(photo.url);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = photo.name || 'photo';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(photo.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Box className="gallery-shell">
      <Container maxWidth="lg" className="gallery-wrap">
        <Box className="gallery-top">
          <Button component={RouterLink} to="/" className="gallery-back-btn">
            Back to Itinerary
          </Button>
          <Typography variant="h4" className="gallery-title">
            Photo Gallery
          </Typography>
          <Typography className="gallery-count">{photoCountLabel}</Typography>
        </Box>

        <Box className="gallery-controls">
          <Button component="label" variant="contained" className="upload-btn" disabled={uploading}>
            {uploading ? 'Uploading...' : 'Upload Photos'}
            <input hidden accept="image/*" multiple type="file" onChange={handleUpload} />
          </Button>
          {uploading ? <CircularProgress size={20} /> : null}
          {message ? <Typography className="gallery-message">{message}</Typography> : null}
        </Box>

        {loading ? (
          <Box className="gallery-loading">
            <CircularProgress size={28} />
            <Typography>Loading photos...</Typography>
          </Box>
        ) : (
          <Box className="photo-grid">
            {photos.map((photo) => (
              <Box key={photo.id} className="photo-card">
                <img src={photo.url} alt={photo.name || 'Gallery item'} className="photo-img" loading="lazy" />
                <Box className="photo-actions">
                  <Button
                    variant="outlined"
                    size="small"
                    className="download-btn"
                    onClick={() => handleDownload(photo)}
                  >
                    Download
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    className="open-btn"
                    component="a"
                    href={photo.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default GalleryPage;
